import {Box, Button, Checkbox, DropdownItem, Modal, Typography} from '@viptech/react-components'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useMemo, useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import useUpdateDaysToRetainOnCompany from 'src/hooks/api/companies/useUpdateDaysToRetainOnCompany'
import {useApp} from 'src/layouts/main-layout/contexts/AppContext'
import api, {Company, CompanyFormSnapshot, CreateCompany, UpdateCompany, createCompanyFormSnapshot} from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {Input} from '../../../components/input/Input'

type CompanyRegisterModalProps = {
  isCreate: boolean
  openModal: boolean
  closeModal: () => void
  company: Company
  setCompany: (company: Company) => void
  loadCompanies: Function
  setSaveModalCompany: (idCompany: number) => void
}

const RetentionDaysOptions = [
  {id: '0', label: '0'},
  {id: '1', label: '1'},
  {id: '3', label: '3'},
  {id: '7', label: '7'},
  {id: '10', label: '10'},
  {id: '30', label: '30'},
  {id: '35', label: '35'},
  {id: '180', label: '180'},
]

function CompanyRegisterModal(props: CompanyRegisterModalProps) {
  const {isCreate, openModal, closeModal, company, setCompany, loadCompanies, setSaveModalCompany} = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {enqueueSnackbar} = useSnackbar()
  const {setUpdateExpirationDate} = useApp()

  useEffect(() => {
    if (isCreate) setCompany({...company, name: '', document: '', expirationDate: moment(moment().add(15, 'day').format()).format('YYYY-MM-DD')})
  }, [])

  async function createCompany() {
    setIsLoading(true)
    try {
      const createCompany = new CreateCompany(company)
      const response = await api.company.create(createCompany)
      enqueueSnackbar('Empresa criada com sucesso', {
        variant: 'success',
      })
      localStorage.setItem('needsRefresh', 'true')
      setSaveModalCompany(response.data.data.id!)
      setUpdateExpirationDate((prev) => !prev)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao criar empresa')
    } finally {
      setIsLoading(false)
      closeModal()
    }
  }

  const {mutateAsync: updateDays} = useUpdateDaysToRetainOnCompany()
  const hasConfigAdvancedPermission = ValidateUserPermissions('camera-parameter-config:update')

  const [initialSnapshot, setInitialSnapshot] = useState<CompanyFormSnapshot | null>(null)

  useEffect(() => {
    if (openModal && !isCreate) {
      setInitialSnapshot(createCompanyFormSnapshot(company))
    } else if (!openModal) {
      setInitialSnapshot(null)
    }
  }, [openModal, isCreate, company.id])

  const hasCompanyFieldChanges = useMemo(() => {
    if (isCreate || !initialSnapshot) return false
    return UpdateCompany.fromEdit(company, initialSnapshot) !== null
  }, [company, initialSnapshot, isCreate])

  const hasRetentionChanges = useMemo(() => {
    if (isCreate || !initialSnapshot || !hasConfigAdvancedPermission) return false
    return (
      company.daysToRetainEvents !== initialSnapshot.daysToRetainEvents ||
      company.daysToRetainImagens !== initialSnapshot.daysToRetainImagens ||
      company.daysToRetainStream !== initialSnapshot.daysToRetainStream
    )
  }, [company, initialSnapshot, isCreate, hasConfigAdvancedPermission])

  const canSave = isCreate ? company.name !== '' && company.document !== '' : hasCompanyFieldChanges || hasRetentionChanges

  async function updateCompany() {
    setIsLoading(true)
    try {
      const updatePayload = initialSnapshot ? UpdateCompany.fromEdit(company, initialSnapshot) : null
      if (updatePayload) {
        await api.company.update(updatePayload)
      }
      if (hasRetentionChanges) {
        await updateDays({id: company.id, body: company})
      }
      if (updatePayload || hasRetentionChanges) {
        enqueueSnackbar('Empresa atualizada com sucesso', {
          variant: 'success',
        })
        loadCompanies()
        setUpdateExpirationDate((prev) => !prev)
      }
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao atualizar empresa')
    } finally {
      setIsLoading(false)
      closeModal()
    }
  }

  const [retainDaysEventsSelected, setRetainDaysEventsSelected] = useState<DropdownItem>()
  const [retainDaysImagensSelected, setRetainDaysImagensSelected] = useState<DropdownItem>()
  const [retainDaysStreamsSelected, setRetainDaysStreamsSelected] = useState<DropdownItem>()

  const handleDefaultDaysRetain = () => {
    if (!isCreate) {
      setRetainDaysEventsSelected({id: String(company.daysToRetainEvents), label: String(company.daysToRetainEvents)})
      setRetainDaysImagensSelected({id: String(company.daysToRetainImagens), label: String(company.daysToRetainImagens)})
      setRetainDaysStreamsSelected({id: String(company.daysToRetainStream), label: String(company.daysToRetainStream)})
      return
    }
    setRetainDaysEventsSelected(RetentionDaysOptions[6])
    setRetainDaysImagensSelected(RetentionDaysOptions[3])
    setRetainDaysStreamsSelected(RetentionDaysOptions[0])
  }

  useEffect(() => {
    handleDefaultDaysRetain()
  }, [isCreate])

  return (
    <Modal isOpen={openModal} closeModal={() => closeModal()} backgroundColor='white' width='600px'>
      <Box display='flex' direction='column' padding='20px' rowGap='25px'>
        <Typography variant='span' size='16px' weight={500}>
          {isCreate ? 'Adicionar' : 'Editar'} Empresa
        </Typography>
        <BtnClose onClose={closeModal} />

        <Box display='flex' direction='column' rowGap='20px'>
          <Input.Root
            label='Nome'
            labelFontSize='12px'
            controlled
            value={company.name}
            borderColor='#D0D5DD'
            onChange={(e) => setCompany({...company, name: e.target.value})}
          />
          <Input.Root
            label='CNPJ/CPF'
            labelFontSize='12px'
            value={company.document}
            controlled
            borderColor='#D0D5DD'
            onChange={(e) => setCompany({...company, document: e.target.value})}
            mask={company.document.length >= 14 ? '00.000.000/0000-00' : '000.000.000-00'}
          />

          <Input.Root
            label='Data de Expiração'
            labelFontSize='12px'
            type={'date'}
            controlled
            value={company.expirationDate?.split('T')[0] ?? ''}
            borderColor='#D0D5DD'
            onChange={(e) => setCompany({...company, expirationDate: e.target.value})}
          />

          <Checkbox
            labelSize='12px'
            color='#344054'
            label='Prova de conceito (POC)'
            checked={company.isPoc ?? false}
            onChange={() => setCompany({...company, isPoc: !(company.isPoc ?? false)})}
          />

          {hasConfigAdvancedPermission && (
            <Box width='100%' display='flex' direction='column' rowGap='20px' marginTop='10px'>
              <Box width='100%' display='flex' direction='column'>
                <Typography variant='span' size='16px' style={{fontWeight: 500}} children='Retenção de Imagens e Eventos' />
                <Typography
                  variant='p'
                  size='12px'
                  color='#8E8E8E'
                  children='Defina o período de retenção, em dias, para o armazenamento de eventos, imagens e vídeos'
                />
              </Box>

              <Box display='flex' columnGap='8px'>
                <Dropdown
                  label='Eventos e Detecções'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysEventsSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysEventsSelected(e)
                    setCompany({...company, daysToRetainEvents: Number(e.id)})
                  }}
                />

                <Dropdown
                  label='Imagens'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysImagensSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysImagensSelected(e)
                    setCompany({...company, daysToRetainImagens: Number(e.id)})
                  }}
                />

                <Dropdown
                  label='Vídeos'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysStreamsSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysStreamsSelected(e)
                    setCompany({...company, daysToRetainStream: Number(e.id)})
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box display='flex' align='center' width='100%' justifyContent='flex-end' columnGap='15px' marginTop='20px'>
          <Button fontSize='12px' height='40px' width='150px' variant='outlined' color='#8E8E8E' onClick={() => closeModal()}>
            Cancelar
          </Button>
          <Button
            fontSize='12px'
            disabledTextColor='#8E8E8E'
            height='40px'
            width='150px'
            disabled={!canSave}
            isLoading={isLoading}
            onClick={() => {
              if (isCreate) createCompany()
              else updateCompany()
            }}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default CompanyRegisterModal
